import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';
import { getBlogPostBySlug } from '../blog/posts';

function formatPublishedDate(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const canonicalUrl = `https://mentaratutors.com/blog/${post.slug}/`;

  return (
    <>
      <title>{post.title} | Mentara Blog</title>
      <meta name="description" content={post.description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={`${post.title} | Mentara Blog`} />
      <meta property="og:description" content={post.description} />
      <meta property="og:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />
      <meta property="article:published_time" content={`${post.publishedAt}T00:00:00-04:00`} />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={`${post.title} | Mentara Blog`} />
      <meta property="twitter:description" content={post.description} />
      <meta property="twitter:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.description,
            datePublished: `${post.publishedAt}T00:00:00-04:00`,
            author: {
              '@type': 'Organization',
              name: post.author,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Mentara',
              logo: {
                '@type': 'ImageObject',
                url: 'https://mentaratutors.com/logos/mentara-full-light.png',
              },
            },
            mainEntityOfPage: canonicalUrl,
          }),
        }}
      />

      <div className="bg-background">
        <section className="py-20 md:py-24 px-4">
          <article className="container max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="mb-10 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to blog
            </Link>

            <div className="flex justify-center mb-6">
              <MantaRayIcon className="h-14 w-14 animate-swim" />
            </div>
            <header className="text-center">
              <div className="mb-4 flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground">
                <Badge variant="secondary">{post.category}</Badge>
                <span>{formatPublishedDate(post.publishedAt)}</span>
                <span className="inline-flex items-center gap-1">
                  <Clock3 className="h-4 w-4" />
                  {post.readTime}
                </span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl leading-tight">{post.title}</h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">{post.description}</p>
              <p className="mt-5 text-sm text-muted-foreground">By {post.author}</p>
            </header>
          </article>
        </section>

        <section className="py-16 px-4 border-t border-border/40">
          <article className="container max-w-3xl mx-auto">
            <div className="prose prose-neutral max-w-none prose-headings:scroll-mt-24 prose-headings:font-semibold prose-p:text-foreground/90 prose-a:text-primary prose-strong:text-foreground dark:prose-invert">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
            </div>
          </article>
        </section>

        <section className="py-20 px-4 border-t border-border/40">
          <div className="container max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">Want more support like this?</h2>
            <p className="text-lg text-muted-foreground">
              Mentara combines expert tutors, flexible scheduling, and practical tools for students,
              parents, and SAT learners.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild>
                <Link to="/register">Get Started</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/blog">Read more articles</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
